import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  ChartBar, Users, Kanban, Bell, Gear, PaperPlaneTilt, SignOut, Compass, ChatCircleDots, Plug, Sliders, ChatTeardropDots, Lightning, X, ChatTeardropText, ArrowsLeftRight, QrCode, Megaphone, PlusCircle, ClipboardText, PhoneCall, Calculator, CalendarCheck, Globe, WhatsappLogo, AndroidLogo, UsersThree, Sparkle, Handshake, Recycle,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { useCompany, COMPANIES } from "@/context/CompanyContext";
import { RecordingSetupModal } from "@/components/CallAiSummary";

const navBase = "flex items-center gap-3 px-4 py-3 md:py-2.5 text-sm border-l-2 transition-colors";
const navActive = "bg-white border-[#002FA7] text-gray-900 font-semibold";
// Amber "attention" state: tints the whole tab while something awaits action
const navHighlight = "bg-[#FFF4E5] border-[#E67E00] text-[#B85F00] font-semibold hover:bg-[#FFE9CC]";
const navIdle = "border-transparent text-gray-700 hover:bg-gray-100";

function Item({ to, icon: Icon, children, testId, onNavigate, badge, badgeClass = "bg-[#E60000] text-white", highlight = false, end = false }) {
  return (
    <NavLink to={to} end={end} data-testid={testId} onClick={onNavigate}
      className={({ isActive }) => `${navBase} ${isActive ? navActive : highlight ? navHighlight : navIdle}`}>
      <Icon size={18} weight="regular" />
      <span className="flex-1">{children}</span>
      {badge > 0 && (
        <span className={`${badgeClass} rounded-full px-1.5 text-[10px] font-bold min-w-[18px] text-center`} data-testid={`${testId}-badge`}>
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </NavLink>
  );
}

export default function Sidebar({ mobileOpen = false, onClose }) {
  const { user, logout } = useAuth();
  const { company, companyLabel, canSwitch, setCompany } = useCompany();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const [pendingTransfers, setPendingTransfers] = useState(0);
  const [pendingLeaves, setPendingLeaves] = useState(0);
  const [pendingQA, setPendingQA] = useState(0);
  // LeadOrbit Android app download (hidden inside the app's own WebView)
  const [appVer, setAppVer] = useState(null);
  const [recHelp, setRecHelp] = useState(false);
  const inAndroidApp = typeof navigator !== "undefined" && /; wv\)/.test(navigator.userAgent || "");
  useEffect(() => {
    if (inAndroidApp) return;
    api.get("/app/version").then(({ data }) => setAppVer(data)).catch(() => {});
  }, [inAndroidApp]);

  // Admin: poll pending leave-request count for the sidebar badge
  useEffect(() => {
    if (!user || user.role !== "admin") return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/leaves", { params: { status: "pending" } });
        if (!stop) setPendingLeaves((data || []).length);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 20000);
    return () => { stop = true; clearInterval(id); };
  }, [user?.id]);

  // Poll the transfer-request count so admins see a badge with pending approvals.
  // Executives see their own pending requests count instead.
  useEffect(() => {
    if (!user) return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/inbox/transfer-requests?status=pending");
        if (!stop) setPendingTransfers((data || []).length);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { stop = true; clearInterval(id); };
  }, [user?.id]);

  // Fast-poll the pending Q&A count so the badge feels realtime.
  // Admins see questions awaiting their answer; executives see their own unanswered questions.
  useEffect(() => {
    if (!user || user.role === "data_entry") return;
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/internal-qa/pending-count");
        if (!stop) setPendingQA(data?.pending || 0);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => { stop = true; clearInterval(id); };
  }, [user?.id]);

  // When a nav item is clicked on mobile, close the drawer
  const handleNavigate = () => { if (mobileOpen && onClose) onClose(); };

  return (
    <>
      {/* Mobile backdrop */}
      <div
        className={`fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity ${mobileOpen ? "opacity-100" : "opacity-0 pointer-events-none"}`}
        onClick={onClose}
        data-testid="sidebar-backdrop"
        aria-hidden={!mobileOpen}
      />
      {/* Sidebar — fixed overlay on mobile, in-flow on md+ */}
      <aside
        className={`
          fixed md:relative top-0 left-0 z-50 md:z-auto h-full
          w-72 md:w-60 shrink-0 bg-gray-50 border-r border-gray-200 flex flex-col
          transform transition-transform duration-200 ease-out
          ${mobileOpen ? "translate-x-0" : "-translate-x-full"} md:translate-x-0
        `}
        data-testid="app-sidebar"
      >
        <div className="px-5 py-5 border-b border-gray-200 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-7 h-7 bg-[#002FA7] flex items-center justify-center shrink-0">
              <Compass size={16} weight="bold" color="white" />
            </div>
            <div className="min-w-0">
              <div className="font-chivo font-black text-sm tracking-tight leading-none">LEADORBIT</div>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mt-0.5 truncate">CRM Control Room</div>
            </div>
          </div>
          {/* Mobile close button */}
          <button
            onClick={onClose}
            className="md:hidden p-1 text-gray-500 hover:text-gray-900"
            data-testid="sidebar-close-btn"
            aria-label="Close menu"
          >
            <X size={18} />
          </button>
        </div>

        {/* Company scope — admins switch, everyone else sees their company */}
        <div className="px-5 py-2.5 border-b border-gray-200" data-testid="company-switcher">
          {canSwitch ? (
            <div className="flex gap-1">
              {Object.entries(COMPANIES).map(([key, label]) => (
                <button key={key} onClick={() => key !== company && setCompany(key)}
                  data-testid={`company-${key}`}
                  className={`flex-1 px-2 py-1.5 text-[10px] uppercase tracking-widest font-bold border transition-colors ${
                    company === key
                      ? (key === "fragvansh"
                          ? "bg-[#7C3AED] text-white border-[#7C3AED]"
                          : "bg-[#002FA7] text-white border-[#002FA7]")
                      : "border-gray-300 text-gray-500 hover:bg-gray-100"}`}>
                  {label}
                </button>
              ))}
            </div>
          ) : (
            <div className={`text-[10px] uppercase tracking-widest font-bold ${company === "fragvansh" ? "text-[#7C3AED]" : "text-[#002FA7]"}`}>
              {companyLabel}
            </div>
          )}
        </div>

        <nav className="flex-1 py-3 space-y-0.5 overflow-y-auto">
          {user?.role !== "data_entry" && (
            <>
              <div className="px-5 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-400 font-bold">Workspace</div>
              <Item to="/dashboard" icon={ChartBar} testId="nav-dashboard" onNavigate={handleNavigate}>Dashboard</Item>
              <Item to="/chat" icon={ChatTeardropDots} testId="nav-chat" onNavigate={handleNavigate}>WhatsApp</Item>
              <Item to="/leads" icon={Kanban} testId="nav-leads" onNavigate={handleNavigate}>Leads</Item>
              {(isAdmin || user?.international_team) && (
                <>
                  <Item to="/international" end icon={Globe} testId="nav-international" onNavigate={handleNavigate}>International</Item>
                  <Item to="/international/chat" icon={WhatsappLogo} testId="nav-international-chat" onNavigate={handleNavigate}>International WhatsApp</Item>
                </>
              )}
              <Item to="/followups" icon={Bell} testId="nav-followups" onNavigate={handleNavigate}>Follow-ups</Item>
              <Item to="/winback" icon={Recycle} testId="nav-winback" onNavigate={handleNavigate}>Win-back calls</Item>
              <Item to="/qa" icon={ChatTeardropText} testId="nav-qa" onNavigate={handleNavigate}
                badge={pendingQA} badgeClass="bg-[#E67E00] text-white" highlight={pendingQA > 0}>Internal Q&amp;A</Item>
              <Item to="/transfer-requests" icon={ArrowsLeftRight} testId="nav-transfer-requests" onNavigate={handleNavigate} badge={pendingTransfers}>Reassign Requests</Item>
              <Item to="/calls" icon={PhoneCall} testId="nav-calls" onNavigate={handleNavigate}>Call Logs</Item>
              <Item to="/team-calls" icon={UsersThree} testId="nav-team-calls" onNavigate={handleNavigate}>Team Calls</Item>
              <Item to="/reports" icon={PaperPlaneTilt} testId="nav-reports" onNavigate={handleNavigate}>Reports</Item>
              <Item to="/ai-reports" icon={Sparkle} testId="nav-ai-reports" onNavigate={handleNavigate}>AI Reports</Item>
              <Item to="/promises" icon={Handshake} testId="nav-promises" onNavigate={handleNavigate}>Promises</Item>
              {!isAdmin && (
                <Item to="/leaves" icon={CalendarCheck} testId="nav-leaves" onNavigate={handleNavigate}>My Leaves</Item>
              )}
            </>
          )}

          {user?.role === "data_entry" && (
            <>
              <div className="px-5 pt-2 pb-1 text-[10px] uppercase tracking-widest text-gray-400 font-bold">Data Entry</div>
              <Item to="/data-entry" icon={PlusCircle} testId="nav-data-entry" onNavigate={handleNavigate}>Lead Entry</Item>
              <Item to="/leaves" icon={CalendarCheck} testId="nav-leaves-de" onNavigate={handleNavigate}>My Leaves</Item>
            </>
          )}

          {isAdmin && (
            <>
              <div className="px-5 pt-4 pb-1 text-[10px] uppercase tracking-widest text-gray-400 font-bold">Admin</div>
              <Item to="/users" icon={Users} testId="nav-users" onNavigate={handleNavigate}>Executives</Item>
              <Item to="/payroll" icon={Calculator} testId="nav-payroll" onNavigate={handleNavigate}>Payroll &amp; Attendance</Item>
              <Item to="/leaves" icon={CalendarCheck} testId="nav-leave-approvals" onNavigate={handleNavigate} badge={pendingLeaves}>Leave Approvals</Item>
              <Item to="/data-entry-inspect" icon={ClipboardText} testId="nav-data-entry-inspect" onNavigate={handleNavigate}>Data Entry Stats</Item>
              <Item to="/alerts" icon={Megaphone} testId="nav-alerts" onNavigate={handleNavigate}>Admin Alerts</Item>
              <Item to="/routing" icon={Gear} testId="nav-routing" onNavigate={handleNavigate}>Routing Rules</Item>
              <Item to="/integrations" icon={Plug} testId="nav-integrations" onNavigate={handleNavigate}>Integrations</Item>
              <Item to="/templates" icon={ChatCircleDots} testId="nav-templates" onNavigate={handleNavigate}>WA Templates</Item>
              <Item to="/chatflows" icon={ChatTeardropDots} testId="nav-chatflows" onNavigate={handleNavigate}>Chatbot Flows</Item>
              <Item to="/quick-replies" icon={Lightning} testId="nav-quick-replies" onNavigate={handleNavigate}>Quick Replies</Item>
              <Item to="/payment-settings" icon={QrCode} testId="nav-payment-settings" onNavigate={handleNavigate}>Payment QR</Item>
              <Item to="/settings" icon={Sliders} testId="nav-settings" onNavigate={handleNavigate}>Settings</Item>
            </>
          )}
        </nav>

        <div className="border-t border-gray-200 p-4">
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">Signed in</div>
          <div className="mt-1 font-semibold text-sm" data-testid="current-user-name">{user?.name}</div>
          <div className="text-xs text-gray-500">
            <span className="kbd">{user?.role}</span> · @{user?.username}
          </div>

          {/* Clock-in from the CRM was removed — attendance is recorded only at
              the office fingerprint device, and being punched in is what grants
              CRM access in the first place. */}

          {user?.role !== "data_entry" && (
            <button onClick={() => setRecHelp(true)} data-testid="recording-setup-btn"
              className="mt-3 w-full flex items-center justify-center gap-2 border border-[#6D28D9] text-[#6D28D9] py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#F5F3FF]">
              <PhoneCall size={14} weight="fill" /> Call recording setup
            </button>
          )}
          {recHelp && <RecordingSetupModal onClose={() => setRecHelp(false)} />}
          {appVer?.apk_url && (
            <a href={appVer.apk_url} download data-testid="download-app-link"
              className="mt-3 w-full flex items-center justify-center gap-2 border border-[#3DDC84] text-[#1B7A47] py-2 text-xs uppercase tracking-wider font-bold hover:bg-[#EAFBF1]"
              title="Install on staff phones (allow 'install unknown apps' when asked)">
              <AndroidLogo size={14} weight="fill" /> Android app{appVer.version_name ? ` v${appVer.version_name}` : ""}
            </a>
          )}

          <button
            className="mt-3 w-full flex items-center justify-center gap-2 border border-gray-300 py-2 text-xs uppercase tracking-wider font-bold hover:bg-gray-100"
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="logout-btn"
          >
            <SignOut size={14} /> Log out
          </button>
        </div>
      </aside>
    </>
  );
}
